import React, { useState, useRef, useEffect } from 'react';
import { chatService } from '../services/chatService';
import '../styles/Chat.css';

const Chat = ({ listingId, listingTitle }) => {
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        
        if (!inputMessage.trim() || isLoading) return;

        const userMessage = {
            id: Date.now(),
            type: 'user',
            content: inputMessage,
            timestamp: new Date().toISOString()
        };

        // Add user message to chat
        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setIsLoading(true);
        setError(null);

        try {
            const response = await chatService.askQuestion(listingId, inputMessage);
            
            if (response.status === 'success') {
                const aiMessage = {
                    id: response.data.id,
                    type: 'ai',
                    content: response.data.ai_answer,
                    timestamp: response.data.created_at
                };
                setMessages(prev => [...prev, aiMessage]);
            } else {
                setError(response.message || 'Failed to get response');
            }
        } catch (err) {
            setError('Failed to send message. Please try again.');
            console.error('Chat error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const formatTime = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    return (
        <div className="chat-container">
            <div className="chat-header">
                <h3>Ask about "{listingTitle}"</h3>
                <p className="chat-subtitle">Get instant answers about this listing</p>
            </div>

            <div className="chat-messages">
                {messages.length === 0 && (
                    <div className="chat-welcome">
                        <div className="welcome-message">
                            <p>👋 Hi! I'm here to help you learn more about this listing.</p>
                            <p>Ask me anything about the item, price, condition, delivery, or location!</p>
                        </div>
                    </div>
                )}

                {messages.map((message) => (
                    <div 
                        key={message.id} 
                        className={`message ${message.type === 'user' ? 'user-message' : 'ai-message'}`}
                    >
                        <div className="message-content">
                            <p>{message.content}</p>
                            <span className="message-time">
                                {formatTime(message.timestamp)}
                            </span>
                        </div>
                    </div>
                ))}

                {isLoading && (
                    <div className="message ai-message">
                        <div className="message-content">
                            <div className="typing-indicator">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="message error-message">
                        <div className="message-content">
                            <p>❌ {error}</p>
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSendMessage} className="chat-input-form">
                <div className="chat-input-container">
                    <input
                        type="text"
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        placeholder="Ask a question about this listing..."
                        disabled={isLoading}
                        className="chat-input"
                    />
                    <button 
                        type="submit" 
                        disabled={!inputMessage.trim() || isLoading}
                        className="chat-send-button"
                    >
                        {isLoading ? (
                            <span className="send-icon">⏳</span>
                        ) : (
                            <span className="send-icon">➤</span>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Chat; 